import { EditOutlined, PlusOutlined, WarningOutlined } from '@ant-design/icons'
import { normalizeNumber, roundValue } from '@salescore/buff-common'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { useCanForView, useMe } from '@salescore/client-common'
import {
  type CompiledFormViewConfig,
  type CompiledKpiPivotViewConfig,
  type CompiledKpiViewConfig,
  type CompiledSheetViewConfig,
  presetFunctions,
  type PresetMeasureFunctions,
  type ViewQueryField,
  type ViewUISheetColumnMeasureCell,
} from '@salescore/core'
import { ButtonWithTooltip, numberWithDelimiterFilter } from '@salescore/frontend-common'
import { App, Button, Popover, Spin, Tooltip } from 'antd'
import { Array, Number } from 'effect'
import { t } from 'i18next'
import type { ReactElement } from 'react'

import { CELL_WIDTH, type RSheetColumn, type RSheetRecordNode } from '../../../domain/column'
import { useCursorMutation } from '../../../recoil/cursor'
import { useRefetchAggregationQueryResult } from '../../../recoil/records/mutations/useRefetchAggregationQueryResult'
import type { SalescoreObjectAbility, ViewAbility } from '../../../recoil/view/atoms'
import { queryFieldToConfigField } from '../../../recoil/view/mutations/field/util'
import { useViewConfigSheet } from '../../../recoil/viewConfig'
import { useAddMeasureMutation } from '../../hooks/useAddMeasureMutation'
import { RSheetsStyle } from '../../util/RSheetsStyle'
import { ROW_HEIGHT } from '../body/const'
import { buildMeasureFormModalProperties } from '../modal/MeasureFormModal'

export const RSheetsFixedFooterRow = ({
  leftFixed,
  isGoalView,
  columns,
  targetColumns,
  compiledConfig,
  view,
  ability,
  setHasChangeToViewSchema,
}: {
  leftFixed: boolean
  isGoalView: boolean
  columns: Array<RSheetColumn<RSheetRecordNode>>
  targetColumns: Array<RSheetColumn<RSheetRecordNode>>
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const { aggregationQueryRecords: records, loading } = useRefetchAggregationQueryResult(compiledConfig, view)
  const me = useMe()

  if (!me.organization.isEarlyReleaseTarget) {
    return <></>
  }

  const widths = targetColumns.map((c) => c.width ?? CELL_WIDTH)

  // sheetのfooterとしてaggregationsを表示する時、以下のようにレコードをマージできる(dimensionsが存在しないため)
  const aggregatedValues = records.reduce((accumulator, x) => ({ ...accumulator, ...x.attributes }), {})

  const maxMeasureNumber = Math.max(...columns.map((c) => c.measures?.length ?? 0))

  if (targetColumns.length === 0 && leftFixed) {
    // flexを使っている関係で、!leftFixedなときは描画しないとレイアウトが崩れる
    return <></>
  }

  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: 固定カラムへの対応が必要
  return (
    <div
      style={{
        position: 'sticky',
        ...(leftFixed ? { left: 0 } : {}),
        zIndex: leftFixed ? RSheetsStyle.zIndex.fixedFooterRowWhenLeftFixed : RSheetsStyle.zIndex.fixedFooterRow,
        display: 'flex',
        backgroundColor: 'white',
        height: ROW_HEIGHT * Math.max(1, maxMeasureNumber),
        width: Number.sumAll(widths),
        // minWidth: '100%', // どうしよ？
        border: `1px #EDF1F2 solid`,
        // verticalAlign: 'middle'
      }}
      data-e2e={`rsheets-fixed-footer-row-${leftFixed ? 'left-fixed' : 'not-fixed'}`}
    >
      {targetColumns.map((column) => {
        const measures = column.measures ?? []
        return (
          <div
            key={column.key}
            className="border border-solid border-gray-100"
            style={{
              backgroundColor: '#fafafa',
              width: column.width,
              overflow: 'hidden',
              height: '100%',
            }}
          >
            {(Math.max(1, maxMeasureNumber) - 1 >= 0
              ? Array.range(0, Math.max(1, maxMeasureNumber) - 1)
              : Array.empty<number>()
            ).map((index) => {
              const measure = measures[index]
              if (measure !== undefined) {
                return (
                  <CalculatorCell
                    key={`${column.key}-${index}`}
                    field={column.field}
                    record={aggregatedValues}
                    measure={measure}
                    loading={loading}
                    isGoalView={isGoalView}
                    view={view}
                    ability={ability}
                    setHasChangeToViewSchema={setHasChangeToViewSchema}
                  />
                )
              }
              if (isGoalView) {
                // eslint-disable-next-line @eslint-react/no-missing-key
                return <></>
              }
              return (
                <AddMeasureButton
                  key={`${column.key}-add-${index}`}
                  column={column}
                  view={view}
                  ability={ability}
                  setHasChangeToViewSchema={setHasChangeToViewSchema}
                />
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

const CalculatorCell = ({
  measure,
  field,
  record,
  loading,
  isGoalView,
  view,
  ability,
  setHasChangeToViewSchema,
}: {
  measure: ViewUISheetColumnMeasureCell
  record: Record<string, unknown>
  field: ViewQueryField | undefined
  loading: boolean
  isGoalView: boolean
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const { modal } = App.useApp()
  const config = useViewConfigSheet()
  const { clearCursor } = useCursorMutation(ability)
  const configField = field === undefined ? undefined : queryFieldToConfigField(field, config.fields ?? [])
  const canDisplayConfigEdit = useCanForView('display-config-edit', view)
  const error = (
    <div className="w-full text-xs text-gray-100" style={{ height: ROW_HEIGHT }}>
      <Button
        danger
        size="small"
        className="size-full text-center"
        type="text"
        icon={<WarningOutlined />}
        onClick={() => {
          clearCursor()
          if (configField !== undefined) {
            const m = modal.confirm(
              buildMeasureFormModalProperties({
                field: configField,
                measureName: measure.name,
                onFinish: () => {
                  m.destroy()
                },
                view,
                setHasChangeToViewSchema,
              }),
            )
          }
        }}
      >
        {t(`エラー`)}
      </Button>
    </div>
  )

  if (loading) {
    return (
      <div className="flex w-full items-center justify-center align-middle" style={{ height: ROW_HEIGHT }}>
        <Spin />
      </div>
    )
  }

  if (field === undefined || configField === undefined) {
    return <span>field undefined</span>
  }

  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: loading
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
  if (record === undefined) {
    return <span>loading</span>
  }

  const value = record[measure.name]
  if (typeof value !== 'string' && typeof value !== 'number' && value !== null) {
    return error
  }

  const content = (
    <Tooltip title={canDisplayConfigEdit ? null : t(`権限がありません`)}>
      <div className="cursor-pointer hover:bg-gray-100" style={{ height: ROW_HEIGHT }}>
        <div className="scale-75 text-xs text-gray-400" style={{ height: 20 }}>
          {measure.label}
        </div>
        <div className="px-1 text-right font-bold" style={{ marginTop: -4 }}>
          {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            numberWithDelimiterFilter(roundValue(normalizeNumber(value ?? undefined))) ?? '-'
          }
        </div>
      </div>
    </Tooltip>
  )

  if (isGoalView) {
    return <>{content}</>
  }
  return (
    <Popover
      // デフォルトだとpadidngが入り、これがattributeだと制御不可能なので、antd.lessに以下クラスを書いた
      classNames={{ root: 'popover-without-padding' }}
      title={t(`集計を追加`)}
      trigger={canDisplayConfigEdit ? 'click' : []}
      content={
        <div>
          <Button
            className="w-full justify-start"
            icon={<PlusOutlined />}
            onClick={() => {
              clearCursor()

              const m = modal.confirm(
                buildMeasureFormModalProperties({
                  field: configField,
                  onFinish: () => {
                    m.destroy()
                  },
                  view,
                  setHasChangeToViewSchema,
                }),
              )
            }}
            type="text"
          >
            {t(`新規追加`)}
          </Button>
          <br />
          <Button
            className="w-full justify-start"
            icon={<EditOutlined />}
            onClick={() => {
              clearCursor()

              const m = modal.confirm(
                buildMeasureFormModalProperties({
                  field: configField,
                  measureName: measure.name,
                  onFinish: () => {
                    m.destroy()
                  },
                  view,
                  setHasChangeToViewSchema,
                }),
              )
            }}
            type="text"
          >
            {t(`編集`)}
          </Button>
        </div>
      }
    >
      {content}
    </Popover>
  )
}

const AddMeasureButton = ({
  column,
  view,
  ability,
  setHasChangeToViewSchema,
}: {
  column: RSheetColumn<RSheetRecordNode>
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const { modal } = App.useApp()
  const addMeasure = useAddMeasureMutation(setHasChangeToViewSchema)
  const { clearCursor } = useCursorMutation(ability)
  const config = useViewConfigSheet()

  const { field } = column
  const canDisplayConfigEdit = useCanForView('display-config-edit', view)

  // ありえないはず
  if (field === undefined) {
    return <></>
  }

  if (column.isSystem === true) {
    return <></>
  }

  const onAddClick = (functionName: PresetMeasureFunctions, label: string) => () => {
    const configField = queryFieldToConfigField(field, config.fields ?? [])
    if (configField === undefined) {
      return
    }
    addMeasure({
      field: configField,
      measure: {
        type: 'preset',
        name: [field.name, functionName].join('_'),
        label,
        function: functionName,
        fieldName: field.name,
      },
    })
  }

  return (
    <Popover
      // デフォルトだとpadidngが入り、これがattributeだと制御不可能なので、antd.lessに以下クラスを書いた
      classNames={{ root: 'popover-without-padding' }}
      title={t(`集計を追加`)}
      trigger={canDisplayConfigEdit ? 'click' : []}
      content={
        <div>
          {presetFunctions().map(({ value, label, availableFieldType }) => {
            if (availableFieldType !== undefined && !availableFieldType.includes(field.meta.fieldType)) {
              // eslint-disable-next-line @eslint-react/no-missing-key
              return <></>
            }
            // countUniqueはルートノードでは表示しない（意味がないので）
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (value === 'countUnique' && (column.node.path ?? []).length < 2) {
              // eslint-disable-next-line @eslint-react/no-missing-key
              return <></>
            }
            return (
              // eslint-disable-next-line @eslint-react/no-missing-key
              <>
                <Button className="w-full justify-start" type="text" onClick={onAddClick(value, label)}>
                  {label}
                </Button>
                <br />
              </>
            )
          })}
          <Button
            className="w-full justify-start"
            onClick={() => {
              clearCursor()
              if (column.field === undefined) {
                return
              }
              const configField = queryFieldToConfigField(column.field, config.fields ?? [])
              if (configField === undefined) {
                return
              }
              const m = modal.confirm(
                buildMeasureFormModalProperties({
                  field: configField,
                  onFinish: () => {
                    m.destroy()
                  },
                  view,
                  setHasChangeToViewSchema,
                }),
              )
            }}
            type="text"
          >
            {t(`詳細設定`)}
          </Button>
          <br />
        </div>
      }
    >
      <ButtonWithTooltip
        icon={<PlusOutlined />}
        type="text"
        disabled={!canDisplayConfigEdit}
        showTooltip={!canDisplayConfigEdit}
        tooltipTitle={t(`権限がありません`)}
        className="m-0 w-full cursor-pointer text-xs text-gray-600 opacity-0 hover:bg-gray-100 hover:opacity-100"
        style={{ height: ROW_HEIGHT }}
      >
        {t(`集計を追加`)}
      </ButtonWithTooltip>
    </Popover>
  )
}
